import { FC, useContext } from 'react';

// Components
import { ConfigContext, OddsDisplayV2 } from '@velinsi/smp-widgets-ui';
import MatchParticipantsHorizontal from './MatchParticipantsHorizontal';
import MatchParticipantsVertical from './MatchParticipantsVertical';

// Hooks/Utils
import { useProcessedOdds } from '../../../../../../packages/odds/hooks/useProcessedOdds';
import useOverlapOdds from '../../../../../../packages/hooks/useOverlapOdds';
import { breakText, classNames, defaultHandleEventClick } from '@sportal365/smp-widget-utils';

// Types/Constants
import { INormalizedMatchV2, ISubscriberInfo } from '../../../../../../packages/types';
import { GenericLayout } from '../../../../../../packages/constants/generic';

interface Props {
  match: INormalizedMatchV2;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
  withBackgroundColor?: boolean;
  withBorderRadius?: boolean;
}

const Match: FC<Props> = ({ match, handleClick, withBackgroundColor, withBorderRadius }) => {
  const { dataAllowInlineOdds, dataBettingLogoOverlap, dataOddsBettingId, dataOddsDisplay } = useContext(ConfigContext);

  const { date, startTime, isLive, url, target, status, odds } = match;

  const normalizedOdds = useProcessedOdds({ odds: odds, dataOddsBettingId, showOdds: dataOddsDisplay });
  const hasOdds = normalizedOdds && normalizedOdds.length > 0;

  const { areBettingOddsOverlapVisible } = useOverlapOdds({ dataBettingLogoOverlap });

  const handleEventClick = url ? () => defaultHandleEventClick(handleClick, url) : undefined;

  return (
    <div className='match-wrapper'>
      <div
        className={classNames({
          'match-container w-full items-center mb-px': true,
          'bg-matchRowBgColor': withBackgroundColor,
          'bg-rowBackgroundColor': !withBackgroundColor,
          'rounded-b-4': withBorderRadius,
          'grid grid-cols-1 lg:grid-cols-[1fr_400px]': dataAllowInlineOdds === true,
          'grid grid-cols-1': dataAllowInlineOdds !== true,
        })}
      >
        <a href={url} target={target} onClick={handleEventClick} className='grid grid-cols-[75px_1fr] w-full items-center no-underline'>
          <div className='match-information text-2 text-center py-4 pl-2 pr-4'>
            <p className='match-date m-0 pb-1 text-[#8c8c8c]'>
              <span className='hidden lg:inline'>{date}</span>
              <span className='lg:hidden'>{breakText(date)}</span>
            </p>
            <span
              className={classNames({
                'lg:hidden match-start-time text-[#8c8c8c]': startTime,
                hidden: !startTime,
              })}
            >
              {startTime}
            </span>
            <span
              className={classNames({
                'match-status font-semibold': true,
                'text-[#8BC34A]': isLive,
                'text-[#8c8c8c]': !isLive,
              })}
            >
              {status}
            </span>
          </div>
          {dataAllowInlineOdds ? (
            <>
              <div className='hidden lg:block'>
                <MatchParticipantsHorizontal {...match} />
              </div>
              <div className='lg:hidden'>
                <MatchParticipantsVertical {...match} />
              </div>
            </>
          ) : (
            <div>
              <MatchParticipantsVertical {...match} />
            </div>
          )}
        </a>
        {hasOdds ? (
          <div
            className={classNames('odds-container', {
              'border-l border-solid border-primaryBackgroundColor': dataAllowInlineOdds && hasOdds,
              'py-2 border-t border-solid border-primaryBackgroundColor': true,
              'lg:py-0 lg:border-t-0': dataAllowInlineOdds && hasOdds,
            })}
          >
            <OddsDisplayV2
              alignment={GenericLayout.HORIZONTAL}
              fullOdds={normalizedOdds}
              handleClick={handleClick}
              marketSelectionDisplay={false}
              areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
              isTransparent={withBackgroundColor}
            />
          </div>
        ) : (
          dataAllowInlineOdds && (
            <div className='empty-odds-container w-full h-full border-l border-solid border-primaryBackgroundColor hidden lg:block' />
          )
        )}
      </div>
    </div>
  );
};

export default Match;
